import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { newSelect as NewSelect } from '../ui/newSelect';
import { getStatementPeriodPresets } from '../utils/billingStatement';
import { Actions as PAYMENT_ACTIONS } from '../../store/actions/paymentActions';
import BillingStatementView from './BillingStatementView';

const presetOptions = getStatementPeriodPresets().map(({ value, label }) => ({ value, label }));

const BillingStatementSection = ({
  mode = 'customer',
  orgId,
  stripeCustomerId,
  statement,
  gettingStatement,
  generatingStatementPdf,
  getStripeStatement,
  getStripeStatementPdf,
  getAdminOrgStatement,
  getAdminOrgStatementPdf,
  showStripeCustomerId = false,
  showTitle = true,
}) => {
  const defaultPreset = getStatementPeriodPresets()[1];
  const [preset, setPreset] = useState(defaultPreset.value);
  const [from, setFrom] = useState(defaultPreset.from);
  const [to, setTo] = useState(defaultPreset.to);

  const presetsByValue = useMemo(() => {
    const map = {};
    getStatementPeriodPresets().forEach((p) => {
      map[p.value] = p;
    });
    return map;
  }, []);

  const handlePresetChange = (value) => {
    setPreset(value);
    if (value !== 'custom') {
      const selected = presetsByValue[value];
      if (selected) {
        setFrom(selected.from);
        setTo(selected.to);
      }
    }
  };

  const fetchStatement = useCallback(() => {
    if (!from || !to) return;
    if (mode === 'admin' && orgId) {
      getAdminOrgStatement({ orgId, from, to });
    } else if (mode === 'customer') {
      getStripeStatement({ from, to });
    }
  }, [from, to, mode, orgId, getAdminOrgStatement, getStripeStatement]);

  const handleDownloadPdf = () => {
    if (!from || !to) return;
    if (mode === 'admin' && orgId) {
      getAdminOrgStatementPdf({ orgId, from, to });
    } else {
      getStripeStatementPdf({ from, to });
    }
  };

  useEffect(() => {
    if (mode === 'customer' && !stripeCustomerId) return;
    if (mode === 'admin' && !orgId) return;
    if (!from || !to) return;
    fetchStatement();
  }, [mode, orgId, stripeCustomerId, from, to, fetchStatement]);

  if (mode === 'customer' && !stripeCustomerId) {
    return null;
  }

  return (
    <div className={showTitle ? 'p-3 mb-3 border-b border-border' : ''}>
      {showTitle && (
        <>
          <h3 className='text-sm font-semibold text-foreground mb-3'>Account statement</h3>
          <p className='text-xs text-muted-foreground mb-4'>
            Period-based summary of invoices, payments, credits, and refunds. Download a PDF for your records.
          </p>
        </>
      )}

      <div className='flex flex-wrap items-end gap-4 mb-4'>
        <div className='w-48'>
          <Label className='text-xs text-muted-foreground mb-1 block'>Period</Label>
          <NewSelect
            options={presetOptions}
            value={preset}
            onChange={handlePresetChange}
            placeholder='Select period'
          />
        </div>
        {preset === 'custom' && (
          <>
            <div>
              <Label htmlFor='statement-from' className='text-xs text-muted-foreground mb-1 block'>
                From
              </Label>
              <Input
                id='statement-from'
                type='date'
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className='w-40 bg-input'
              />
            </div>
            <div>
              <Label htmlFor='statement-to' className='text-xs text-muted-foreground mb-1 block'>
                To
              </Label>
              <Input
                id='statement-to'
                type='date'
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className='w-40 bg-input'
              />
            </div>
          </>
        )}
        <Button variant='secondary' size='sm' onClick={fetchStatement} loading={gettingStatement}>
          Refresh
        </Button>
      </div>

      <BillingStatementView
        statement={statement}
        loading={gettingStatement}
        generatingPdf={generatingStatementPdf}
        onDownloadPdf={handleDownloadPdf}
        showStripeCustomerId={showStripeCustomerId}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  const { statement, gettingStatement, generatingStatementPdf, stripeCustomerId } = state.payment?.stripe || {};
  return {
    statement,
    gettingStatement,
    generatingStatementPdf,
    stripeCustomerId,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getStripeStatement: (params) => dispatch(PAYMENT_ACTIONS.getStripeStatement(params)),
  getStripeStatementPdf: (params) => dispatch(PAYMENT_ACTIONS.getStripeStatementPdf(params)),
  getAdminOrgStatement: (params) => dispatch(PAYMENT_ACTIONS.getAdminOrgStatement(params)),
  getAdminOrgStatementPdf: (params) => dispatch(PAYMENT_ACTIONS.getAdminOrgStatementPdf(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BillingStatementSection);
